import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import './Registration.css';
import iconImage from '../assets/images/icon.png';
import { buildSessionFromLoginResponse, upsertSession, setActiveSession } from '../utils/accountSessions';

export default function Registration() {
    const [formData, setFormData] = useState({
        full_name: "",
        username: "",
        password: "",
        phone_number: "",
        field_id: "",
        gender: ""
    });
    const [fields, setFields] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [usernameError, setUsernameError] = useState("");
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    axios.defaults.withCredentials = true;

    const fetchFields = useCallback(async () => {
        try {
            const response = await axios.get(API_URL + 'fetch-fields');
            setFields(response.data);
        } catch (err) {
            console.error('Error fetching fields:', err);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    const validateUsername = (username) => {
        const lowercaseUsername = username.toLowerCase();
        if (username !== lowercaseUsername) {
            return "Username must be lowercase only";
        }
        if (username.length < 7 || username.length > 10) {
            return "Username must be between 7 and 10 characters";
        }
        if (!/^[a-z]+$/.test(username)) {
            return "Username must contain only lowercase English letters";
        }
        return "";
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === "username") {
            const lowercaseValue = value.toLowerCase();
            setFormData({ ...formData, [name]: lowercaseValue });
            const error = validateUsername(lowercaseValue);
            setUsernameError(error);
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const usernameValidationError = validateUsername(formData.username);
        if (usernameValidationError) {
            setError(usernameValidationError);
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(API_URL + "register", formData);

            const session = buildSessionFromLoginResponse(response.data);
            if (session) {
                upsertSession(session);
                setActiveSession(session.id);
            } else {
                localStorage.setItem("userInfo", JSON.stringify({
                    user: response.data.user,
                    doctor: response.data.doctor,
                    role: response.data.role
                }));
            }

            setSuccess("Registration successful! Redirecting...");
            
            setTimeout(() => {
                if (response.data.role === 'admin') {
                    navigate('/control/doctor-list');
                } else {
                    navigate('/control/appointments');
                }
            }, 1500);
        } catch (err) {
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                if (errors.username) {
                    setError("Username already exists. Please choose another username.");
                } else {
                    setError(Object.values(errors).flat().join(', '));
                }
            } else {
                setError("An error occurred during registration. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-container" dir="ltr">
            <div className="registration-card">
                <div className="registration-header">
                    <div className="registration-icon">
                        <img src={iconImage} alt="Clinic Icon" className="registration-icon-image" />
                    </div>
                    <h1 className="registration-title">Create New Account</h1>
                    <p className="registration-subtitle">Join Aullan Bro as a Doctor</p>
                </div>
                
                <div className="registration-body">
                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-triangle"></i>
                            {error}
                        </div>
                    )}
                    
                    {success && (
                        <div className="success-message">
                            <i className="fas fa-check-circle"></i>
                            {success}
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                        <div className="registration-form-group">
                            <label className="registration-form-label">Full Name</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="text"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    className="registration-form-control"
                                    placeholder="Enter your full name"
                                    required
                                />
                                <i className="fas fa-user registration-input-icon"></i>
                            </div>
                        </div>
                        
                        <div className="registration-form-group">
                            <label className="registration-form-label">Username</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    className="registration-form-control"
                                    placeholder="Enter username (7-10 lowercase letters)"
                                    required
                                    minLength="7"
                                    maxLength="10"
                                />
                                <i className="fas fa-at registration-input-icon"></i>
                            </div>
                            {usernameError && (
                                <div className="username-hint username-error">
                                    <i className="fas fa-times-circle"></i> {usernameError}
                                </div>
                            )}
                            {!usernameError && formData.username.length >= 7 && (
                                <div className="username-hint username-success">
                                    <i className="fas fa-check-circle"></i> Username is valid
                                </div>
                            )}
                        </div>
                        
                        <div className="registration-form-group">
                            <label className="registration-form-label">Password</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="registration-form-control"
                                    placeholder="Enter password (at least 6 characters)"
                                    required
                                    minLength="6"
                                />
                                <i className="fas fa-lock registration-input-icon"></i>
                            </div>
                        </div>
                        
                        <div className="registration-form-group">
                            <label className="registration-form-label">Phone Number</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="tel"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    className="registration-form-control"
                                    placeholder="Enter phone number"
                                    required
                                />
                                <i className="fas fa-phone registration-input-icon"></i>
                            </div>
                        </div>
                        
                        <div className="registration-form-group">
                            <label className="registration-form-label">Specialization</label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    name="field_id"
                                    value={formData.field_id}
                                    onChange={handleChange}
                                    className="registration-form-select"
                                    required
                                >
                                    <option value="">Select specialization</option>
                                    {fields.map(field => (
                                        <option key={field.field_id} value={field.field_id}>
                                            {field.name}
                                        </option>
                                    ))}
                                </select>
                                <i className="fas fa-stethoscope registration-input-icon"></i>
                            </div>
                        </div>
                        
                        <div className="registration-form-group">
                            <label className="registration-form-label">Gender</label>
                            <div style={{ position: 'relative' }}>
                                <select 
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    className="registration-form-select"
                                    required
                                >
                                    <option value="">Select gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </select>
                                <i className="fas fa-venus-mars registration-input-icon"></i>
                            </div>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="registration-btn"
                            disabled={loading || usernameError}
                        >
                            <div className="btn-content">
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Registering...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-user-plus"></i>
                                        Create Account
                                    </>
                                )}
                            </div>
                        </button>
                        
                        <div className="registration-login-prompt">
                            <span>Already have an account? </span>
                            <Link to="/control/login" className="login-link">
                                Login
                            </Link>
                        </div>
                    </form>
                </div>
                
                <div className="registration-footer">
                    <p>Aullan Bro - Secure, Advanced, and Easy to Use</p>
                </div>
            </div>
        </div>
    );
}
